import { getExtensionValue, getReferenceString } from '@medplum/core';
import { Coding, Location, PractitionerRole, Reference, Schedule, Slot } from '@medplum/fhirtypes';
import { SCHEDULING_GUIDELINE_PARAMETER, SCHEDULING_PARAMETERS_EXTENSION } from '@bariendo/lib/extensions';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const NO_PROVIDER = '—';

/** A single procedure availability window, flattened for table display. */
export interface TimeBlockRow {
  slotId: string;
  /** UTC ISO start/end, preserved for editing. */
  start: string;
  end: string;
  timezone: string;
  /** Display labels in the slot's own timezone. */
  dateLabel: string;
  startTimeLabel: string;
  endTimeLabel: string;
  providerName: string;
  practitionerRef?: string;
  locationName: string | null;
  locationRef?: string;
  procedureTypes: Coding[];
  guideline?: string;
}

export interface TimeBlockFilters {
  locationRefs?: string[];
  procedureTypeCodes?: string[];
  practitionerRefs?: string[];
}

export interface TimeBlockContext {
  schedules: Schedule[];
  practitionerRoles: PractitionerRole[];
  locations: Location[];
}

function getSchedulingExtension(slot: Slot): { timezone?: string; locationRef?: string; guideline?: string } {
  const ext = slot.extension?.find((e) => e.url === SCHEDULING_PARAMETERS_EXTENSION);
  if (!ext) {
    return {};
  }
  const locationRef = (getExtensionValue(ext, 'location') as Reference<Location> | undefined)?.reference;
  return {
    timezone: getExtensionValue(ext, 'timezone') as string | undefined,
    locationRef,
    guideline: getExtensionValue(ext, SCHEDULING_GUIDELINE_PARAMETER) as string | undefined,
  };
}

/** Build flattened table rows from free availability Slots, resolving provider and location from context. */
export function buildTimeBlockRows(slots: Slot[], context: TimeBlockContext): TimeBlockRow[] {
  const scheduleById = new Map(context.schedules.map((s) => [getReferenceString(s), s]));
  const roleById = new Map(context.practitionerRoles.map((r) => [getReferenceString(r), r]));
  const locationById = new Map(context.locations.map((l) => [getReferenceString(l), l]));

  return slots.map((slot) => {
    const { timezone: tz, locationRef, guideline } = getSchedulingExtension(slot);
    const resolvedTz = tz || 'UTC';

    const schedule = slot.schedule?.reference ? scheduleById.get(slot.schedule.reference) : undefined;
    const roleRef = schedule?.actor?.find((a) => a.reference?.startsWith('PractitionerRole/'))?.reference;
    const role = roleRef ? roleById.get(roleRef) : undefined;
    const practitionerRef = role?.practitioner?.reference;
    const providerName = role?.practitioner?.display ?? NO_PROVIDER;

    const location = locationRef ? locationById.get(locationRef) : undefined;

    const startInTz = slot.start ? dayjs.utc(slot.start).tz(resolvedTz) : undefined;
    const endInTz = slot.end ? dayjs.utc(slot.end).tz(resolvedTz) : undefined;

    const procedureTypes: Coding[] =
      slot.serviceType?.flatMap((st) => st?.coding?.filter((c): c is Coding => !!c) ?? []) ?? [];

    return {
      slotId: slot.id!,
      start: slot.start ?? '',
      end: slot.end ?? '',
      timezone: resolvedTz,
      dateLabel: startInTz ? startInTz.format('MMM D, YYYY') : '',
      startTimeLabel: startInTz ? startInTz.format('h:mm A') : '',
      endTimeLabel: endInTz ? endInTz.format('h:mm A') : '',
      providerName,
      practitionerRef,
      locationName: location?.name ?? null,
      locationRef: location ? locationRef : undefined,
      procedureTypes,
      guideline,
    };
  });
}

/** Apply location / procedure-type / provider filters with AND semantics. Empty filters pass everything. */
export function filterTimeBlockRows(rows: TimeBlockRow[], filters: TimeBlockFilters): TimeBlockRow[] {
  return rows.filter((row) => {
    if (filters.locationRefs?.length && (!row.locationRef || !filters.locationRefs.includes(row.locationRef))) {
      return false;
    }
    if (
      filters.procedureTypeCodes?.length &&
      !row.procedureTypes.some((c) => c.code && filters.procedureTypeCodes!.includes(c.code))
    ) {
      return false;
    }
    if (
      filters.practitionerRefs?.length &&
      (!row.practitionerRef || !filters.practitionerRefs.includes(row.practitionerRef))
    ) {
      return false;
    }
    return true;
  });
}
